import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Plus, ListTree, Sparkles, Filter, MoreHorizontal, Users, Trash2, Edit2, Globe2, AtSign } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '../../lib/api';

// Curated emoji & color palettes for list customisation (QG-Chatting-inspired)
const EMOJIS = ['📁', '🔥', '⭐', '💎', '🐳', '💸', '🎯', '👑', '💝', '🚀', '⚡', '❤️', '💋', '🌹', '🍑', '🍒', '✨', '🎁', '📌', '🔖', '💰', '🏆', '👀', '🤑', '📊', '📈', '🛍️', '💳', '🎲', '🔔', '🥇', '🥈', '🥉', '📅', '⏰', '🎉', '🎊', '🥳', '🌟', '💫', '🌈'];
const COLORS = [
  { key: 'indigo', css: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30', dot: 'bg-indigo-400' },
  { key: 'violet', css: 'bg-violet-500/15 text-violet-300 border-violet-500/30', dot: 'bg-violet-400' },
  { key: 'fuchsia', css: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30', dot: 'bg-fuchsia-400' },
  { key: 'pink', css: 'bg-pink-500/15 text-pink-300 border-pink-500/30', dot: 'bg-pink-400' },
  { key: 'rose', css: 'bg-rose-500/15 text-rose-300 border-rose-500/30', dot: 'bg-rose-400' },
  { key: 'amber', css: 'bg-amber-500/15 text-amber-300 border-amber-500/30', dot: 'bg-amber-400' },
  { key: 'yellow', css: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30', dot: 'bg-yellow-400' },
  { key: 'emerald', css: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', dot: 'bg-emerald-400' },
  { key: 'teal', css: 'bg-teal-500/15 text-teal-300 border-teal-500/30', dot: 'bg-teal-400' },
  { key: 'cyan', css: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30', dot: 'bg-cyan-400' },
  { key: 'sky', css: 'bg-sky-500/15 text-sky-300 border-sky-500/30', dot: 'bg-sky-400' },
  { key: 'slate', css: 'bg-slate-500/15 text-slate-300 border-slate-500/30', dot: 'bg-slate-400' },
];

const DEFAULT_FORM = { name: '', description: '', emoji: '📁', color: COLORS[0].css, scope: 'global', account_id: null };
const DEFAULT_SMART = { name: '', description: '', emoji: '📊', color: COLORS[0].css, rule: '', rule_field: 'days_inactive', rule_op: '>=', rule_value: 7, rule_value2: null, scope: 'global', account_id: null };

// Structured rule builder — selection only, no free text (prevents broken rules)
export const RULE_FIELDS = [
  { value: 'days_inactive', label: 'Days without activity', unit: 'days' },
  { value: 'fan_age_days', label: 'Fan age (since first contact)', unit: 'days' },
  { value: 'total_spent', label: 'Total spent', unit: '€' },
];
export const RULE_OPS = [
  { value: '>=', label: '≥ at least' },
  { value: '<=', label: '≤ at most' },
  { value: '==', label: '= exactly' },
  { value: 'between', label: 'between (range)' },
];
export const buildRuleLabel = (field, op, value, value2) => {
  const f = RULE_FIELDS.find(x => x.value === field);
  if (!f) return '';
  if (op === 'between') return `${f.label} between ${value} and ${value2} ${f.unit}`;
  return `${f.label} ${op === '>=' ? '≥' : op === '<=' ? '≤' : '='} ${value} ${f.unit}`;
};

export default function Lists() {
  const { t } = useTranslation();
  const [manual, setManual] = useState([]);
  const [smart, setSmart] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [editing, setEditing] = useState(null); // {kind: 'manual'|'smart', item}

  const load = useCallback(async () => {
    try {
      const [m, s, a] = await Promise.all([api.get('/lists/manual'), api.get('/lists/smart'), api.get('/accounts')]);
      setManual(m.data);
      setSmart(s.data);
      setAccounts(a.data);
    } catch (e) {
      toast.error(apiError(e, t('dash.lists.loadFailed')));
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const delManual = async (id) => {
    try {
      await api.delete(`/lists/manual/${id}`);
      setManual(manual.filter(x => x.id !== id));
      toast.success(t('dash.lists.deleted'));
    } catch (e) { toast.error(apiError(e, t('dash.lists.deleteFailed'))); }
  };

  const delSmart = async (id) => {
    try {
      await api.delete(`/lists/smart/${id}`);
      setSmart(smart.filter(x => x.id !== id));
      toast.success(t('dash.lists.smartDeleted'));
    } catch (e) { toast.error(apiError(e, t('dash.lists.deleteFailed'))); }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="lists-page">
      <PageHeader
        title={t('dash.lists.title')}
        subtitle={t('dash.lists.subtitle', { manual: manual.length, smart: smart.length })}
      />

      <Tabs defaultValue="manual" className="space-y-4">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger data-testid="manual-lists-tab" value="manual" className="data-[state=active]:bg-white/10"><ListTree size={14} className="mr-1.5" /> {t('dash.lists.tabManual', { count: manual.length })}</TabsTrigger>
          <TabsTrigger data-testid="smart-lists-tab" value="smart" className="data-[state=active]:bg-white/10"><Sparkles size={14} className="mr-1.5" /> {t('dash.lists.tabSmart', { count: smart.length })}</TabsTrigger>
        </TabsList>

        <TabsContent value="manual">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-slate-400">{t('dash.lists.helpManual')}</div>
            <NewListDialog kind="manual" accounts={accounts} onCreated={load} />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {manual.map(l => (
              <ListCard key={l.id} item={l} kind="manual" accounts={accounts}
                        onEdit={() => setEditing({ kind: 'manual', item: l })}
                        onDelete={() => delManual(l.id)} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="smart">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-slate-400">{t('dash.lists.helpSmart')}</div>
            <NewListDialog kind="smart" accounts={accounts} onCreated={load} />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {smart.map(l => (
              <ListCard key={l.id} item={l} kind="smart" accounts={accounts}
                        onEdit={() => setEditing({ kind: 'smart', item: l })}
                        onDelete={() => delSmart(l.id)} />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {editing && (
        <EditListDialog
          kind={editing.kind}
          item={editing.item}
          accounts={accounts}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            if (editing.kind === 'manual') setManual(manual.map(x => x.id === updated.id ? updated : x));
            else setSmart(smart.map(x => x.id === updated.id ? updated : x));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ListCard({ item: l, kind, accounts, onEdit, onDelete }) {
  const { t } = useTranslation();
  const account = l.account_id ? accounts.find(a => a.id === l.account_id) : null;
  const scopeBadge = l.scope === 'account' && account
    ? { icon: <AtSign size={9} />, label: account.name, cls: 'border-sky-500/30 bg-sky-500/10 text-sky-300' }
    : { icon: <Globe2 size={9} />, label: t('dash.lists.global'), cls: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' };
  const colorCss = l.color || COLORS[0].css;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-indigo-500/30 transition-colors" data-testid={`${kind}-list-${l.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl border flex items-center justify-center text-xl ${colorCss}`}>{l.emoji || (kind === 'smart' ? '📊' : '📁')}</div>
          <div>
            <div className="text-base font-semibold text-white flex items-center gap-2">
              {l.name}
              {/* 26/09 : cette liste se remplit toute seule (étiquettes automatiques,
                  listes du bot) — le badge évite de la croire tenue à la main. */}
              {l.auto && (
                <span data-testid={`liste-auto-${l.id}`} title="Cette liste se remplit automatiquement"
                  className="text-[9px] font-semibold uppercase tracking-wide text-indigo-300 bg-indigo-500/15 border border-indigo-500/30 rounded-full px-1.5 py-px">
                  auto
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 flex items-center gap-1"><Users size={10} /> {t('dash.lists.clients', { count: l.count })}</div>
          </div>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:bg-white/5"><MoreHorizontal size={15} /></Button>
      </div>
      {l.description && <p className="text-xs text-slate-400 mb-3 line-clamp-2" data-testid={`${kind}-list-desc-${l.id}`}>{l.description}</p>}
      {kind === 'smart' && l.rule && (
        <div className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2 flex items-center gap-2 mb-3">
          <Filter size={12} className="text-indigo-400 shrink-0" />
          <span className="text-xs text-slate-300 font-mono truncate">{l.rule}</span>
        </div>
      )}
      <div className="flex items-center justify-between pt-3 border-t border-white/5 gap-2">
        <Badge variant="outline" className={`${scopeBadge.cls} text-[10px]`} data-testid={`${kind}-list-scope-${l.id}`}>
          {scopeBadge.icon}<span className="ml-1">{scopeBadge.label}</span>
        </Badge>
        <div className="flex gap-1.5">
          <Button data-testid={`edit-${kind}-list-${l.id}`} size="sm" variant="outline" className="border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 h-7 text-xs" onClick={onEdit}><Edit2 size={11} className="mr-1" /> {t('dash.lists.edit')}</Button>
          <Button data-testid={`delete-${kind}-list-${l.id}`} size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-400 hover:bg-rose-500/10" onClick={onDelete}><Trash2 size={11} /></Button>
        </div>
      </div>
    </div>
  );
}

// ---- Shared form body (emoji + color + scope + name + description [+ rule]) ----
function ListFormBody({ form, setForm, kind, accounts }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-[auto_1fr] gap-3 items-start">
        <div className="space-y-1.5">
          <Label className="text-slate-300 text-xs">{t('dash.lists.iconLabel')}</Label>
          <div className={`w-14 h-14 rounded-xl border flex items-center justify-center text-2xl ${form.color}`} data-testid="list-icon-preview">
            {form.emoji}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-300 text-xs">{t('dash.lists.nameLabel')}</Label>
          <Input data-testid="list-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" placeholder={kind === 'smart' ? 'VIP Clients' : 'Top Tippers'} />
          <Label className="text-slate-300 text-xs pt-1.5">{t('dash.lists.descLabel')}</Label>
          <Input data-testid="list-desc-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-white/5 border-white/10 text-white h-9 text-xs" placeholder={t('dash.lists.descPlaceholder')} />
        </div>
      </div>

      <div>
        <Label className="text-slate-300 text-xs mb-1.5 block">{t('dash.lists.emojiLabel')}</Label>
        <div className="grid grid-cols-10 gap-1 max-h-32 overflow-y-auto rounded-lg border border-white/10 bg-white/[0.02] p-2" data-testid="emoji-grid">
          {EMOJIS.map((e, idx) => (
            <button key={`${idx}-${e}`} type="button" data-testid={`emoji-${e}`}
              onClick={() => setForm({ ...form, emoji: e })}
              className={`w-8 h-8 rounded-md text-lg flex items-center justify-center transition-colors ${form.emoji === e ? 'bg-indigo-500/30 ring-1 ring-indigo-400' : 'hover:bg-white/10'}`}>
              {e}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-slate-300 text-xs mb-1.5 block">{t('dash.lists.colorLabel')}</Label>
        <div className="flex flex-wrap gap-2" data-testid="color-grid">
          {COLORS.map(c => (
            <button key={c.key} type="button" data-testid={`color-${c.key}`}
              onClick={() => setForm({ ...form, color: c.css })}
              className={`w-7 h-7 rounded-full border-2 transition-all ${form.color === c.css ? 'border-white scale-110' : 'border-white/20'}`}
              style={{ background: '' }}
              aria-label={c.key}
              >
              <div className={`w-full h-full rounded-full ${c.dot}`} />
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-slate-300 text-xs mb-1.5 block">{t('dash.lists.scopeLabel')}</Label>
        <div className="grid grid-cols-2 gap-2" data-testid="scope-toggle">
          <button type="button" data-testid="scope-global"
            onClick={() => setForm({ ...form, scope: 'global', account_id: null })}
            className={`p-3 rounded-lg border text-left transition-colors ${form.scope === 'global' ? 'border-emerald-400/50 bg-emerald-500/10' : 'border-white/10 bg-white/[0.02] hover:bg-white/5'}`}>
            <div className="flex items-center gap-2 text-sm text-white font-medium"><Globe2 size={14} className="text-emerald-400" /> {t('dash.lists.scopeGlobal')}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{t('dash.lists.scopeGlobalHelp')}</div>
          </button>
          <button type="button" data-testid="scope-account"
            onClick={() => setForm({ ...form, scope: 'account', account_id: form.account_id || accounts[0]?.id || null })}
            className={`p-3 rounded-lg border text-left transition-colors ${form.scope === 'account' ? 'border-sky-400/50 bg-sky-500/10' : 'border-white/10 bg-white/[0.02] hover:bg-white/5'}`}>
            <div className="flex items-center gap-2 text-sm text-white font-medium"><AtSign size={14} className="text-sky-400" /> {t('dash.lists.scopeAccount')}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{t('dash.lists.scopeAccountHelp')}</div>
          </button>
        </div>
        {form.scope === 'account' && (
          <select data-testid="scope-account-select"
            value={form.account_id || ''}
            onChange={e => setForm({ ...form, account_id: e.target.value || null })}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white h-9 outline-none mt-2 focus:border-sky-400/50">
            {accounts.length === 0 && <option value="" className="bg-[#0B0B0E]">{t('dash.lists.noAccounts')}</option>}
            {accounts.map(a => <option key={a.id} value={a.id} className="bg-[#0B0B0E]">{a.name}</option>)}
          </select>
        )}
      </div>

      {kind === 'smart' && (
        <div className="space-y-1.5">
          <Label className="text-slate-300 text-xs">{t('dash.lists.ruleLabel')}</Label>
          <select data-testid="smart-rule-field" value={form.rule_field || 'days_inactive'}
            onChange={e => { const v = e.target.value; setForm({ ...form, rule_field: v, rule: buildRuleLabel(v, form.rule_op || '>=', form.rule_value ?? 7, form.rule_value2) }); }}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 text-sm text-white h-10 outline-none focus:border-indigo-500/50">
            {RULE_FIELDS.map(f => <option key={f.value} value={f.value} className="bg-[#0B0B0E]">{f.label}</option>)}
          </select>
          <div className="grid grid-cols-12 gap-2">
            <select data-testid="smart-rule-op" value={form.rule_op || '>='}
              onChange={e => { const v = e.target.value; setForm({ ...form, rule_op: v, rule: buildRuleLabel(form.rule_field || 'days_inactive', v, form.rule_value ?? 7, form.rule_value2) }); }}
              className={`${form.rule_op === 'between' ? 'col-span-4' : 'col-span-6'} bg-white/5 border border-white/10 rounded-lg px-2 text-sm text-white h-10 outline-none focus:border-indigo-500/50`}>
              {RULE_OPS.map(o => <option key={o.value} value={o.value} className="bg-[#0B0B0E]">{o.label}</option>)}
            </select>
            <Input data-testid="smart-rule-value" type="number" min="0" value={form.rule_value ?? ''}
              placeholder={form.rule_op === 'between' ? 'min' : 'value'}
              onChange={e => { const v = e.target.value === '' ? '' : Number(e.target.value); setForm({ ...form, rule_value: v, rule: buildRuleLabel(form.rule_field || 'days_inactive', form.rule_op || '>=', v, form.rule_value2) }); }}
              className={`${form.rule_op === 'between' ? 'col-span-4' : 'col-span-6'} bg-white/5 border-white/10 text-white h-10`} />
            {form.rule_op === 'between' && (
              <Input data-testid="smart-rule-value2" type="number" min="0" value={form.rule_value2 ?? ''}
                placeholder="max"
                onChange={e => { const v = e.target.value === '' ? '' : Number(e.target.value); setForm({ ...form, rule_value2: v, rule: buildRuleLabel(form.rule_field || 'days_inactive', 'between', form.rule_value ?? 0, v) }); }}
                className="col-span-4 bg-white/5 border-white/10 text-white h-10" />
            )}
          </div>
          <div className="text-[11px] text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 rounded-lg px-2.5 py-1.5">
            {buildRuleLabel(form.rule_field || 'days_inactive', form.rule_op || '>=', form.rule_value ?? 7, form.rule_value2) || t('dash.lists.pickRule')}
          </div>
        </div>
      )}
    </div>
  );
}

function NewListDialog({ kind, accounts, onCreated }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(kind === 'smart' ? DEFAULT_SMART : DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('dash.lists.nameReq')); return; }
    if (kind === 'smart' && (form.rule_value === '' || form.rule_value === null || form.rule_value === undefined)) { toast.error(t('dash.lists.ruleValueReq')); return; }
    if (form.scope === 'account' && !form.account_id) { toast.error(t('dash.lists.selectAccount')); return; }
    setSaving(true);
    try {
      const body = { ...form, name: form.name.trim() };
      if (kind === 'smart') {
        body.rule_value = Number(form.rule_value);
        body.rule_value2 = (form.rule_op === 'between' && form.rule_value2 !== '' && form.rule_value2 != null) ? Number(form.rule_value2) : null;
        body.rule = buildRuleLabel(form.rule_field, form.rule_op, body.rule_value, body.rule_value2);
      }
      await api.post(kind === 'smart' ? '/lists/smart' : '/lists/manual', body);
      setOpen(false);
      setForm(kind === 'smart' ? DEFAULT_SMART : DEFAULT_FORM);
      toast.success(kind === 'smart' ? t('dash.lists.createdSmart') : t('dash.lists.createdManual'));
      onCreated();
    } catch (e) {
      toast.error(apiError(e, t('dash.lists.createFailed')));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`create-${kind}-list-button`} className="bg-indigo-500 hover:bg-indigo-600">
          <Plus size={16} className="mr-1.5" /> {kind === 'smart' ? t('dash.lists.createSmart') : t('dash.lists.createManual')}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">{kind === 'smart' ? t('dash.lists.createTitleSmart') : t('dash.lists.createTitleManual')}</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            {kind === 'smart' ? t('dash.lists.createDescSmart') : t('dash.lists.createDescManual')}
          </DialogDescription>
        </DialogHeader>
        <ListFormBody form={form} setForm={setForm} kind={kind} accounts={accounts} />
        <Button data-testid="list-save-button" className="w-full bg-indigo-500 hover:bg-indigo-600 mt-4" onClick={save} disabled={saving}>
          {saving ? t('dash.lists.creating') : t('dash.lists.create')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function EditListDialog({ kind, item, accounts, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (item) setForm({
      name: item.name,
      description: item.description || '',
      emoji: item.emoji || (kind === 'smart' ? '📊' : '📁'),
      color: item.color || COLORS[0].css,
      scope: item.scope || 'global',
      account_id: item.account_id || null,
      ...(kind === 'smart' ? {
        rule: item.rule || '',
        rule_field: item.rule_field || 'days_inactive',
        rule_op: item.rule_op || '>=',
        rule_value: item.rule_value ?? 7,
        rule_value2: item.rule_value2 ?? null,
      } : {}),
    });
  }, [item, kind]);

  if (!item) return null;

  const save = async () => {
    if (!form.name?.trim()) { toast.error(t('dash.lists.nameReq')); return; }
    if (form.scope === 'account' && !form.account_id) { toast.error(t('dash.lists.selectAccount')); return; }
    setSaving(true);
    try {
      const body = { ...form };
      if (kind === 'smart' && body.rule_field) {
        body.rule_value = Number(body.rule_value);
        body.rule_value2 = (body.rule_op === 'between' && body.rule_value2 !== '' && body.rule_value2 != null) ? Number(body.rule_value2) : null;
        body.rule = buildRuleLabel(body.rule_field, body.rule_op, body.rule_value, body.rule_value2);
      }
      const { data } = await api.patch(`/lists/${kind}/${item.id}`, body);
      toast.success(t('dash.lists.updated'));
      onSaved(data);
    } catch (e) { toast.error(apiError(e, t('dash.lists.updateFailed'))); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">{kind === 'smart' ? t('dash.lists.editTitleSmart') : t('dash.lists.editTitleManual')}</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">{t('dash.lists.editDesc')}</DialogDescription>
        </DialogHeader>
        <ListFormBody form={form} setForm={setForm} kind={kind} accounts={accounts} />
        <Button data-testid="edit-list-save-button" className="w-full bg-indigo-500 hover:bg-indigo-600 mt-4" onClick={save} disabled={saving}>
          {saving ? t('dash.lists.saving') : t('dash.lists.save')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
